/**
 * JSON-LD schema.org markup for Tameen.
 *
 * The Organization / InsuranceAgency schema is the primary trust signal
 * Google uses for E-A-T (Expertise, Authoritativeness, Trustworthiness) in
 * regulated industries like insurance. The image/text FSA logo and insurer
 * logos on the homepage are visually obvious but not machine-readable as
 * credentials or partnerships — this file makes them so.
 *
 * Pass `organizationSchema` (and `websiteSchema`) to
 * `<SeoHelmet schema={...} />` on the homepage. On top product pages,
 * use `buildProductServiceSchema()` to produce a Service entity whose
 * `provider` references the Organization `@id`.
 */

const SITE_URL = "https://tameen.om";

// Insurer partners rendered in SectionFour on the homepage. Keeping this list
// alongside the schema so both stay in sync when partners are added/removed.
const INSURER_PARTNERS = [
  "Takaful Oman Insurance",
  "OQIC",
  "GIG",
  "Arabia Insurance",
  "Dhofar Insurance",
  "MIC",
  "The New India Assurance Co. Ltd",
  "Orient Insurance PJSC",
  "Liva Insurance",
  "Al Madina Takaful",
];

// Top product lines surfaced on the homepage. Each links to its Seo landing.
const TOP_PRODUCTS = [
  { name: "Motor Insurance", url: `${SITE_URL}/motor-insurance` },
  { name: "Travel Insurance", url: `${SITE_URL}/travel-insurance` },
  { name: "Health Insurance", url: `${SITE_URL}/health-insurance` },
  { name: "Home Insurance", url: `${SITE_URL}/home-insurance` },
  { name: "Life Insurance", url: `${SITE_URL}/life-insurance` },
];

export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "InsuranceAgency",
  "@id": `${SITE_URL}/#organization`,
  name: "Tameen",
  legalName: "ONEIC Tameen SPC",
  alternateName: "Tameen.om",
  url: SITE_URL,
  logo: {
    "@type": "ImageObject",
    url: "https://storage.tameen.om/tameen/newtm/tameen_newlogo.webp",
    width: 512,
    height: 512,
  },
  image: "https://storage.tameen.om/tameen/newtm/tameen_newlogo.webp",
  description:
    "Oman's leading digital insurance platform. Compare and buy motor, travel, health, and property insurance online from Oman's top insurers.",
  slogan: "New-age partner for all your Insurance needs",
  foundingDate: "2023",
  email: "care@tameen.om",
  telephone: "+968-72227122",
  address: {
    "@type": "PostalAddress",
    streetAddress: "ONEIC Head Office Building, 3rd Floor, Al Khuwair South",
    addressLocality: "Muscat",
    addressRegion: "Muscat Governorate",
    addressCountry: "OM",
  },
  areaServed: { "@type": "Country", name: "Oman" },
  sameAs: [
    "https://www.facebook.com/tameen.om.insurance",
    "https://www.instagram.com/tameen.om/",
    "https://www.linkedin.com/company/tameen-om",
    "https://www.snapchat.com/add/tameen.om",
  ],
  parentOrganization: {
    "@type": "Organization",
    name: "ONEIC",
    alternateName: "Oman National Engineering & Investment Company",
    url: "http://eservicesprd.oneic.ws/",
  },
  hasCredential: [
    {
      "@type": "EducationalOccupationalCredential",
      credentialCategory: "license",
      name: "Insurance Broker License",
      identifier: "1513944",
      recognizedBy: {
        "@type": "GovernmentOrganization",
        name: "Financial Services Authority of Oman",
        alternateName: "FSA Oman",
        url: "https://fsa.gov.om",
      },
      url: "https://fsa.gov.om/Home/DisplayCompanyDetails?companyType=2&companyId=5430&selectedCompanyType=2",
    },
  ],
  makesOffer: TOP_PRODUCTS.map((p) => ({
    "@type": "Offer",
    itemOffered: {
      "@type": "Service",
      name: p.name,
      url: p.url,
      provider: { "@id": `${SITE_URL}/#organization` },
    },
  })),
  brand: INSURER_PARTNERS.map((name) => ({
    "@type": "Organization",
    name,
  })),
};

// WebSite entity — helps Google understand the site-search intent and
// enables the Sitelinks Search Box in search results.
export const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE_URL}/#website`,
  url: SITE_URL,
  name: "Tameen",
  alternateName: "Tameen.om",
  publisher: { "@id": `${SITE_URL}/#organization` },
  inLanguage: ["en", "ar"],
};

/**
 * Per-product Service schema. Each top product page (Motor, Travel, Health,
 * Home, Life) calls this to attach a Service entity whose `provider` points
 * back at the InsuranceAgency `@id` from `organizationSchema`. That gives
 * Google a linked graph — the org owns the offerings, the offerings live on
 * their own URLs — rather than five isolated Service records.
 */
export const buildProductServiceSchema = ({
  name,
  nameAr,
  description,
  url,
  serviceType,
  lang,
}) => {
  const isAr =
    lang === "ar" ||
    (typeof window !== "undefined" &&
      /^\/ar(\/|$)/.test(window.location.pathname));
  const localizedUrl =
    isAr && url && !url.includes("/ar")
      ? url.replace("https://tameen.om", "https://tameen.om/ar")
      : url;

  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${localizedUrl}#service`,
    name,
    alternateName: nameAr,
    description,
    serviceType: serviceType || name,
    url: localizedUrl,
    provider: { "@id": `${SITE_URL}/#organization` },
    areaServed: { "@type": "Country", name: "Oman" },
    availableLanguage: ["en", "ar"],
  };
};
